import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthMembership, AuthUser, Locale, MeResponse, Permission } from '@saniplus/contracts';

import type { ApiError } from '../api/apiError';
import {
  bootstrapSession,
  changePin,
  login,
  logout,
  refreshSession,
  setUserLocale,
} from './authThunks';

export type AuthStatus =
  /** Nothing attempted yet. */
  | 'idle'
  /** `bootstrapSession` in flight — the router must not decide anything yet. */
  | 'bootstrapping'
  | 'authenticating'
  | 'authenticated'
  /**
   * Rehydrated from the offline identity snapshot: a cold start that could not
   * reach the server (`auth/sessionSnapshot.ts`). There is a person to render
   * the shell for and a user id to scope the punch queue to, but **nothing is
   * verified** — `accessToken` is `null`, `selectIsAuthenticated` is `false`,
   * and every request still needs a token only the server can mint. The first
   * refresh that gets through promotes it to `authenticated` or ends it.
   */
  | 'offline'
  | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  /** Carries `role` — the one authority field (Phase 4, `users.role`). */
  user: AuthUser | null;
  /**
   * Scope only: which contracts (and which of their sites) the session may
   * reach. A membership carries no role, so nothing here decides what the
   * caller may do — that is `user.role` and `permissions`.
   */
  memberships: AuthMembership[];
  permissions: Permission[];
  /**
   * In memory only, for the lifetime of the tab (D-05). Nothing in this slice
   * is ever persisted — no redux-persist, no localStorage, no IndexedDB.
   */
  accessToken: string | null;
  mustChangePin: boolean;
  /** `true` on the first-device auto-bind, so the shell can offer install. */
  deviceBoundNow: boolean;
  error: ApiError | null;
}

const initialState: AuthState = {
  status: 'idle',
  user: null,
  memberships: [],
  permissions: [],
  accessToken: null,
  mustChangePin: false,
  deviceBoundNow: false,
  error: null,
};

function clear(state: AuthState): void {
  state.status = 'anonymous';
  state.user = null;
  state.memberships = [];
  state.permissions = [];
  state.accessToken = null;
  state.mustChangePin = false;
  state.deviceBoundNow = false;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** The base query rotated the token behind everyone's back. */
    accessTokenRefreshed(state, action: PayloadAction<string>) {
      state.accessToken = action.payload;
      // A rotation is the server vouching for the session, which is the one
      // thing an offline rehydration was missing.
      if (state.status === 'offline') state.status = 'authenticated';
    },
    /** Refresh failed: the session is unrecoverable, drop everything. */
    sessionExpired(state) {
      clear(state);
      state.error = null;
    },
    errorCleared(state) {
      state.error = null;
    },
    localeApplied(state, action: PayloadAction<Locale>) {
      if (state.user) state.user = { ...state.user, locale: action.payload };
    },
    /**
     * A fresh `GET /auth/me` read mid-session (an app re-reads it after a
     * mutation that changed the caller's own scope, e.g. creating a contract
     * grants the creator a membership). Identity only: the token and the
     * status are not this read's to touch.
     */
    sessionRead(state, action: PayloadAction<MeResponse>) {
      state.user = action.payload.user;
      state.memberships = action.payload.memberships;
      state.permissions = action.payload.permissions;
      state.mustChangePin = action.payload.mustChangePin;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'authenticating';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.accessToken = action.payload.accessToken;
        state.user = action.payload.user;
        state.memberships = action.payload.memberships;
        state.permissions = action.payload.permissions;
        state.mustChangePin = action.payload.mustChangePin;
        state.deviceBoundNow = action.payload.device?.boundNow ?? false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        clear(state);
        state.error = action.payload ?? null;
      })

      .addCase(refreshSession.fulfilled, (state, action) => {
        state.accessToken = action.payload;
        if (state.status === 'offline') state.status = 'authenticated';
      })
      .addCase(refreshSession.rejected, (state, action) => {
        // `status: 0` is a refresh that never reached the server. It refused
        // nothing, so it ends nothing — the session (and, in the worker PWA,
        // the identity its queued punches are scoped to) stays exactly as it
        // was until an answer actually arrives.
        if (action.payload?.status === 0) return;
        clear(state);
      })

      .addCase(bootstrapSession.pending, (state) => {
        state.status = 'bootstrapping';
      })
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        // No token means the identity came from the offline snapshot, not from
        // `/auth/me`: it is a shell to render, not a session to trust.
        state.status = action.payload.accessToken === null ? 'offline' : 'authenticated';
        state.accessToken = action.payload.accessToken;
        state.user = action.payload.me.user;
        state.memberships = action.payload.me.memberships;
        state.permissions = action.payload.me.permissions;
        state.mustChangePin = action.payload.me.mustChangePin;
        state.error = null;
      })
      .addCase(bootstrapSession.rejected, (state) => {
        clear(state);
      })

      .addCase(changePin.fulfilled, (state, action) => {
        state.mustChangePin = false;
        // The old token still claims `mcp: true` and is refused everywhere.
        state.accessToken = action.payload;
        /*
         * And the session is authenticated, said out loud.
         *
         * `selectIsAuthenticated` is `status === 'authenticated' && token !==
         * null`, so a `changePin` that installs a token without restoring the
         * status leaves the caller holding a valid credential that every route
         * guard reads as anonymous — the worker walks the three PIN steps,
         * taps *continuer*, and lands on `/login`. The status was not
         * necessarily `'authenticated'` when we got here: a reload on `/pin`
         * re-enters through `bootstrapSession`, and any 401 in the meantime
         * (the one-time-PIN token is refused on every route but this one)
         * drives `baseQuery` through `sessionExpired`. This route's 200 is the
         * server saying the session is good; the slice now agrees.
         */
        state.status = 'authenticated';
        state.error = null;
      })
      .addCase(changePin.rejected, (state, action) => {
        state.error = action.payload ?? null;
      })

      .addCase(setUserLocale.fulfilled, (state, action) => {
        if (state.user) state.user = { ...state.user, locale: action.payload };
      })

      .addCase(logout.fulfilled, clear)
      .addCase(logout.rejected, clear);
  },
});

export const { accessTokenRefreshed, sessionExpired, errorCleared, localeApplied, sessionRead } =
  authSlice.actions;
export const authReducer = authSlice.reducer;
